import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';
import axios from 'axios';

const FAQSection = () => {
  const { t, language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  const [dbFaqs, setDbFaqs] = useState(null);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const url = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${url}/api/content/faqs`);
        if (res.data && res.data.length > 0) {
          setDbFaqs(res.data);
        }
      } catch (e) {
        // Use fallback
      }
    };
    fetchFaqs();
  }, []);

  // Use DB FAQs if available, otherwise use translations
  const faqItems = dbFaqs
    ? dbFaqs.map(f => ({
        question: f[`question_${language}`] || f.question_de || '',
        answer: f[`answer_${language}`] || f.answer_de || ''
      }))
    : t('faq.items');

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 lg:py-32 bg-gradient-to-b from-grey/5 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
      <div className="absolute top-20 right-10 w-64 h-64 bg-mid-blue/5 rounded-full blur-hero" />

      <div className="max-w-3xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <AnimateOnScroll animation="fade-up">
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-mid-blue/20 to-mid-purple/20 text-mid-blue px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-blue/20">
              <HelpCircle size={14} />{t('faq.badge')}
            </span>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4">
              {t('faq.title')} <span className="gradient-text">{t('faq.titleHighlight')}</span>
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.2}>
            <p className="text-dark-grey dark:text-gray-400 text-lg max-w-2xl mx-auto">{t('faq.subtitle')}</p>
          </AnimateOnScroll>
        </div>

        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <AnimateOnScroll key={index} animation="fade-up" delay={0.1 * index}>
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-grey/20 dark:border-gray-700 overflow-hidden card-hover" data-testid={`faq-item-${index}`}>
                <button onClick={() => toggleItem(index)} className="w-full flex items-center justify-between p-6 text-left" data-testid={`faq-toggle-${index}`}>
                  <span className="font-semibold text-black dark:text-white pr-4">{item.question}</span>
                  <ChevronDown size={20} className={`text-mid-purple flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-6 pb-6 text-dark-grey dark:text-gray-400 leading-relaxed">{item.answer}</p>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
